import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, Plus, Trash2, LayoutGrid, LayoutList, ExternalLink, Play, X, Edit2, Save, ChevronDown, ChevronRight } from 'lucide-react'
import './StrimPartners.css'

// ─── Default partner channels ─────────────────────────────
const DEFAULT_PARTNERS = [
  { id: 'musicfromthe412', channelId: '58789', label: 'Music From The 412' },
]

function getStoredPartners() {
  try {
    const raw = localStorage.getItem('strimm-partners')
    return raw ? JSON.parse(raw) : DEFAULT_PARTNERS
  } catch { return DEFAULT_PARTNERS }
}

function storePartners(partners) {
  localStorage.setItem('strimm-partners', JSON.stringify(partners))
}

// ─── Partner Manager Modal ────────────────────────────────
function PartnerManager({ partners, onClose, onSave }) {
  const [list, setList] = useState([...partners])
  const [newUser, setNewUser] = useState('')
  const [newChannel, setNewChannel] = useState('')
  const [newLabel, setNewLabel] = useState('')

  function addPartner() {
    if (!newUser.trim() || !newChannel.trim()) return
    setList(prev => [...prev, {
      id: newUser.trim(),
      channelId: newChannel.trim(),
      label: newLabel.trim() || newUser.trim()
    }])
    setNewUser(''); setNewChannel(''); setNewLabel('')
  }

  function removePartner(idx) {
    setList(prev => prev.filter((_, i) => i !== idx))
  }

  return (
    <div className="sp-modal-overlay" onClick={onClose}>
      <div className="sp-modal" onClick={e => e.stopPropagation()}>
        <div className="sp-modal-header">
          <h2>Manage Strimm Partners</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="sp-modal-body">
          <table className="sp-partner-table">
            <thead>
              <tr><th>Label</th><th>User</th><th>Channel ID</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((p, i) => (
                <tr key={i}>
                  <td>{p.label}</td>
                  <td><code>{p.id}</code></td>
                  <td><code>{p.channelId}</code></td>
                  <td><button className="btn-ghost btn-sm danger" onClick={() => removePartner(i)}><Trash2 size={12} /></button></td>
                </tr>
              ))}
              <tr className="sp-add-row">
                <td><input placeholder="Label" value={newLabel} onChange={e => setNewLabel(e.target.value)} /></td>
                <td><input placeholder="User slug" value={newUser} onChange={e => setNewUser(e.target.value)} /></td>
                <td><input placeholder="Channel ID" value={newChannel} onChange={e => setNewChannel(e.target.value)} /></td>
                <td><button className="btn-primary btn-sm" onClick={addPartner}><Plus size={12} /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="sp-modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={() => { onSave(list); onClose() }}>
            <Save size={14} /> Save Partners
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Video Detail Panel ───────────────────────────────────
function VideoDetail({ video, onClose }) {
  if (!video) return null

  const hlsUrl = video.providerVideoId || ''
  const isHls = hlsUrl.endsWith('.m3u8') || hlsUrl.includes('.m3u8')

  return (
    <div className="sp-detail">
      <div className="sp-detail-header">
        <h2>{video.title || 'Untitled'}</h2>
        <button className="btn-ghost btn-sm" onClick={onClose}><X size={14} /></button>
      </div>

      {/* HLS Preview */}
      {isHls && (
        <div className="sp-preview-wrap">
          <video
            key={hlsUrl}
            controls
            autoPlay
            muted
            className="sp-hls-player"
            src={hlsUrl}
          >
            Your browser does not support HLS playback.
          </video>
        </div>
      )}

      <div className="sp-detail-meta">
        <div className="sp-meta-row">
          <span className="sp-meta-label">Channel ID</span>
          <span>{video.channelId || '—'}</span>
        </div>
        <div className="sp-meta-row">
          <span className="sp-meta-label">Duration</span>
          <span>{video.duration || '—'}</span>
        </div>
        {video.description && (
          <div className="sp-meta-row sp-meta-desc">
            <span className="sp-meta-label">Description</span>
            <span>{video.description}</span>
          </div>
        )}
        {hlsUrl && (
          <div className="sp-meta-row">
            <span className="sp-meta-label">Stream URL</span>
            <a href={hlsUrl} target="_blank" rel="noopener noreferrer" className="sp-stream-link">
              {hlsUrl.length > 60 ? hlsUrl.substring(0, 60) + '…' : hlsUrl}
              <ExternalLink size={11} />
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Video Table ──────────────────────────────────────────
function VideoTable({ videos, loading, selected, onSelect }) {
  if (loading) return <div className="sp-loading">Loading videos…</div>
  if (!videos.length) return <div className="sp-empty">No videos found for this date.</div>

  return (
    <div className="sp-table-wrap">
      <table className="sp-video-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th>Title</th>
            <th style={{ width: 100 }}>Duration</th>
            <th style={{ width: 100 }}>Channel ID</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v, i) => {
            const isHls = (v.providerVideoId || '').includes('.m3u8')
            const isActive = selected?.providerVideoId === v.providerVideoId && selected?.title === v.title
            return (
              <tr
                key={`${v.providerVideoId}-${i}`}
                className={isActive ? 'active' : ''}
                onClick={() => onSelect(v)}
              >
                <td className="sp-play-cell">
                  {isHls && <Play size={14} className="sp-play-icon" />}
                </td>
                <td className="sp-title-cell">{v.title || 'Untitled'}</td>
                <td>{v.duration || '—'}</td>
                <td><code>{v.channelId}</code></td>
                <td>
                  {v.providerVideoId && (
                    <a href={v.providerVideoId} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      <ExternalLink size={12} />
                    </a>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Video Card List ──────────────────────────────────────
function VideoCardList({ videos, loading, selected, onSelect }) {
  if (loading) return <div className="sp-loading">Loading videos…</div>
  if (!videos.length) return <div className="sp-empty">No videos found for this date.</div>

  return (
    <div className="sp-card-list">
      {videos.map((v, i) => {
        const isHls = (v.providerVideoId || '').includes('.m3u8')
        const isActive = selected?.providerVideoId === v.providerVideoId && selected?.title === v.title
        return (
          <div
            key={`${v.providerVideoId}-${i}`}
            className={`sp-card ${isActive ? 'active' : ''}`}
            onClick={() => onSelect(v)}
          >
            <div className="sp-card-title">{v.title || 'Untitled'}</div>
            <div className="sp-card-meta">
              {v.duration && <span>{v.duration}</span>}
              {isHls && <Play size={12} className="sp-play-icon" />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────
export default function StrimPartners() {
  const [partners, setPartners] = useState(getStoredPartners)
  const [activePartner, setActivePartner] = useState(null)
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0])
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState(null)
  const [showPartnerMgr, setShowPartnerMgr] = useState(false)
  const [viewMode, setViewMode] = useState('table')
  const [searchTerm, setSearchTerm] = useState('')

  // Auto-select first partner
  useEffect(() => {
    if (!activePartner && partners.length) setActivePartner(partners[0])
  }, [partners, activePartner])

  const fetchVideos = useCallback(async () => {
    if (!activePartner) return
    setLoading(true)
    setStatus('')
    setSelected(null)
    try {
      const url = `https://iframe-api.strimm.com/api/v1/services/embed/users/${activePartner.id}/channels/${activePartner.channelId}/video?dates=${date}`
      const res = await fetch(url, { headers: { accept: 'application/json' } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (Array.isArray(data)) {
        setVideos(data)
        setStatus(`✅ ${data.length} video${data.length !== 1 ? 's' : ''} loaded`)
      } else {
        setVideos([])
        setStatus('No video data returned.')
      }
    } catch (e) {
      setVideos([])
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [activePartner, date])

  useEffect(() => { if (activePartner) fetchVideos() }, [fetchVideos, activePartner])

  function handleSavePartners(newList) {
    setPartners(newList)
    storePartners(newList)
    if (!newList.find(p => p.id === activePartner?.id)) {
      setActivePartner(newList[0] || null)
    }
  }

  // Filter videos by search
  const filtered = searchTerm
    ? videos.filter(v => (v.title || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : videos

  return (
    <div className="sp-root">
      <div className="sp-header">
        <div className="sp-header-left">
          <h1>Strimm Partners</h1>
          <span className="sp-count">{filtered.length} videos</span>
        </div>
        <div className="sp-header-actions">
          <div className="sp-view-toggle">
            <button className={`sp-view-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Card view">
              <LayoutGrid size={16} />
            </button>
            <button className={`sp-view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')} title="Table view">
              <LayoutList size={16} />
            </button>
          </div>
          <button className="btn-ghost" onClick={() => setShowPartnerMgr(true)}>
            <Plus size={14} /> Partners ({partners.length})
          </button>
          <button className="btn-primary" onClick={fetchVideos} disabled={loading || !activePartner}>
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`sp-status ${status.startsWith('Error') ? 'error' : ''}`}>
          {status}
          <button className="btn-ghost btn-sm" onClick={() => setStatus('')}>✕</button>
        </div>
      )}

      {/* Filters row: partner select, date picker, search */}
      <div className="sp-filters">
        <div className="filter-group">
          <select value={activePartner?.id || ''} onChange={e => {
            const p = partners.find(p => p.id === e.target.value)
            if (p) setActivePartner(p)
          }}>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.label} ({p.channelId})</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="sp-date-input"
          />
        </div>
        <div className="filter-group search-group">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content Area */}
      {viewMode === 'table' ? (
        <>
          <div className="sp-detail-panel">
            {selected ? (
              <VideoDetail video={selected} onClose={() => setSelected(null)} />
            ) : (
              <div className="sp-detail-empty">
                <p className="muted">Select a video from the table below to view details and preview HLS stream</p>
              </div>
            )}
          </div>
          <div className="sp-table-panel">
            <VideoTable videos={filtered} loading={loading} selected={selected} onSelect={setSelected} />
          </div>
        </>
      ) : (
        <div className="sp-cards-layout">
          <div className="sp-cards-sidebar">
            <VideoCardList videos={filtered} loading={loading} selected={selected} onSelect={setSelected} />
          </div>
          <div className="sp-cards-main">
            {selected ? (
              <VideoDetail video={selected} onClose={() => setSelected(null)} />
            ) : (
              <div className="sp-detail-empty">
                <p className="muted">Select a video to preview HLS stream</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Partner Manager Modal */}
      {showPartnerMgr && (
        <PartnerManager
          partners={partners}
          onClose={() => setShowPartnerMgr(false)}
          onSave={handleSavePartners}
        />
      )}
    </div>
  )
}
